import React, { useEffect, useState } from "react";
import { Modal, Button, Spin, Alert, message } from "antd";
import { Map, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/**
 * 共用：地圖選氣象站
 *
 * props
 *  - open, onClose
 *  - loadStations: () => Promise<{ device: {latitude, longitude, coord_is_default}, stations: [] }>
 *  - selectStation: (stationId) => Promise<any>
 *  - onSelected: (station) => void          選站成功後回呼（更新列上顯示）
 *  - deviceLabel: 地圖上機具標記的文字
 *  - coordHint: 座標是預設值時的提示文字
 */

const makeIcon = (color) =>
  new L.Icon({
    iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${color}.png`,
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
  });

const deviceIcon = makeIcon("red");
const stationIcon = makeIcon("blue");
const selectedStationIcon = makeIcon("green");

const FALLBACK_CENTER = [25.020329, 121.529085];

export default function StationPickerModal({
  open,
  onClose,
  loadStations,
  selectStation,
  onSelected,
  deviceLabel = "📷 機具位置",
  coordHint = "機具座標尚未設定（仍是預設值），附近站點可能不正確，請先設定經緯度。",
}) {
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(null);
  const [device, setDevice] = useState(null);
  const [stations, setStations] = useState([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setStations([]);
    setDevice(null);
    setLoading(true);
    loadStations()
      .then((res) => {
        if (cancelled || res?.code !== 200) return;
        setDevice(res.data.device);
        setStations(res.data.stations || []);
      })
      .catch((err) => !cancelled && message.error(err.uiMessage || "載入氣象站失敗"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // loadStations 由父層每次 render 重建，只依 open 觸發
  }, [open]);

  const handleSelect = async (stationId) => {
    setSelecting(stationId);
    try {
      await selectStation(stationId);
      message.success("已選擇氣象站");
      setStations((prev) => prev.map((s) => ({ ...s, is_selected: s.id === stationId })));
      const picked = stations.find((s) => s.id === stationId);
      if (picked && onSelected) onSelected(picked);
    } catch (err) {
      message.error(err.uiMessage || "選站失敗");
    } finally {
      setSelecting(null);
    }
  };

  const center =
    device?.latitude != null && device?.longitude != null
      ? [device.latitude, device.longitude]
      : FALLBACK_CENTER;

  return (
    <Modal
      title="選擇氣象站"
      open={open}
      onCancel={onClose}
      footer={null}
      width={700}
      bodyStyle={{ padding: "12px" }}
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spin tip="載入氣象站中..." />
        </div>
      ) : (
        <>
          {device?.coord_is_default && (
            <Alert type="warning" showIcon message={coordHint} style={{ marginBottom: 8 }} />
          )}
          <div style={{ height: 380, marginBottom: 12, borderRadius: 6, overflow: "hidden" }}>
            <Map center={center} zoom={12} style={{ height: "100%", width: "100%" }}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="© OpenStreetMap contributors"
              />
              <Marker position={center} icon={deviceIcon}>
                <Popup>{deviceLabel}</Popup>
              </Marker>
              {stations.map((s) => (
                <Marker
                  key={s.id}
                  position={[s.latitude, s.longitude]}
                  icon={s.is_selected ? selectedStationIcon : stationIcon}
                >
                  <Popup>
                    <div style={{ minWidth: 150 }}>
                      <div style={{ fontWeight: "bold", marginBottom: 4 }}>{s.station_name}</div>
                      <div style={{ fontSize: 12, color: "#666" }}>距離 {s.distance} km</div>
                      {(s.temperature != null || s.humidity != null) && (
                        <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                          {s.temperature != null && `🌡 ${s.temperature}°C `}
                          {s.humidity != null && `💧 ${s.humidity}%`}
                        </div>
                      )}
                      <Button
                        type={s.is_selected ? "default" : "primary"}
                        size="small"
                        block
                        loading={selecting === s.id}
                        disabled={s.is_selected}
                        onClick={() => handleSelect(s.id)}
                      >
                        {s.is_selected ? "已選擇" : "選擇此站"}
                      </Button>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </Map>
          </div>
          <div style={{ fontSize: 12, color: "var(--secondary-text)" }}>
            🔴 機具位置　🔵 氣象站　🟢 已選擇的站　點擊標記可選擇氣象站
          </div>
        </>
      )}
    </Modal>
  );
}